import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, ChevronLeft, MessageSquare, Video } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { draftKey, readDraft, writeDraft, clearDraft } from '../../lib/postDraft';
import { suggestVideoFromPost } from '../../lib/videoLibrary';
import SuggestVideoToLibrary, { EMPTY_SUGGESTION } from './SuggestVideoToLibrary';
import PostEditor from './PostEditor';
import TagInput from './TagInput';
import { parseTags } from '../../lib/tags';
import { syncContentImages, imageErrorMessage } from '../../lib/postImages';
import { useUser } from '../../contexts/UserContext';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

const inputStyle = {
  backgroundColor: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
};

// step: 'type' | 'table' | 'compose'
// startStep 'table' skips the post/video chooser — used by "New Topic" on the
// Tables screen, where the intent is already a topic.

// ── Draft persistence ─────────────────────────────────────────
// The modal unmounts on every close path, so a half-written topic used to die
// with it — the single most-reported tester complaint. The draft now lives in
// localStorage and is restored on reopen, which makes every close path
// non-destructive, including the back chevron on the table step (that chevron
// closes the modal when the chooser was skipped, because there is nothing to
// go back to — it just no longer costs you the text).
//
// Storage lives in lib/postDraft so the table detail screen's inline compose
// form — a SECOND, separate compose surface — shares one implementation under
// its own key.
const DRAFT_KEY = draftKey();

const CreatePostModal = ({ onClose, startStep = 'type' }) => {
  const navigate = useNavigate();
  const { session } = useUser();
  const userId = session?.user?.id ?? null;
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Read once on mount. A draft is only worth restoring if it has text in it —
  // a bare table selection is not something anyone would miss.
  const saved = useMemo(() => readDraft(DRAFT_KEY), []);

  const [step, setStep] = useState(saved?.selectedTable ? 'compose' : startStep);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tableSearch, setTableSearch] = useState('');
  const [selectedTable, setSelectedTable] = useState(saved?.selectedTable ?? null);
  const [subject, setSubject] = useState(saved?.subject ?? '');
  const [content, setContent] = useState(saved?.content ?? '');
  const [tags, setTags] = useState(saved?.tags ?? '');
  // Photos are uploaded as they're picked, so the draft only carries their
  // metadata — a restored draft keeps its photos.
  const [images, setImages] = useState(Array.isArray(saved?.images) ? saved.images : []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [restored, setRestored] = useState(!!saved);
  // Not part of the draft: it depends on a video lookup, and re-ticking it is
  // one tap.
  const [videoChoice, setVideoChoice] = useState(EMPTY_SUGGESTION);
  const [videoSuggestion, setVideoSuggestion] = useState(null);
  const handleVideoResolve = useCallback((s) => setVideoSuggestion(s), []);

  useModalDismiss(onClose);

  // Mirror the draft on every keystroke. Cheap — one small JSON write — and it
  // means a hard kill of the WebView (which Capacitor does on memory pressure)
  // loses nothing either.
  useEffect(() => {
    writeDraft(DRAFT_KEY, { subject, content, tags, selectedTable, images });
  }, [subject, content, tags, selectedTable, images]);

  const discardDraft = () => {
    clearDraft(DRAFT_KEY);
    setSubject('');
    setContent('');
    setTags('');
    setImages([]);
    setRestored(false);
  };

  useEffect(() => {
    const fetchTables = async () => {
      const { data, error: rpcError } = await supabase.rpc('get_community_tables_with_membership');
      if (!rpcError) setTables(data ?? []);
      setTablesLoading(false);
    };
    fetchTables();
  }, []);

  const filteredTables = tables
    .filter(t => t.name.toLowerCase().includes(tableSearch.toLowerCase()))
    .sort((a, b) => {
      if (a.is_member && !b.is_member) return -1;
      if (!a.is_member && b.is_member) return 1;
      return (b.member_count || 0) - (a.member_count || 0);
    });

  const handleSubmit = async () => {
    if (submitting) return;   // ⌘/Ctrl+Enter bypasses the disabled button
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    // create_post rejects empty content (005:404) and the column is NOT NULL
    // with a length check — catch it here rather than surfacing the generic
    // failure message.
    if (!content.trim()) {
      setError('Add a message to your post.');
      return;
    }
    if (videoSuggestion && !videoSuggestion.category) {
      setError('Choose a library category for the video, or untick the box.');
      return;
    }
    setSubmitting(true);
    setError('');

    const parsedTags = parseTags(tags);

    const { data: newPostId, error: rpcError } = await supabase.rpc('create_post', {
      p_table_id: selectedTable.id,
      p_subject: subject.trim(),
      p_content: content.trim(),
      p_tags: parsedTags,
    });

    if (rpcError) {
      setSubmitting(false);
      const msg = rpcError.message ?? '';
      setError(
        msg.includes('not_verified') ? 'Verification required to post.' :
        msg.includes('content_required') ? 'Add a message to your post.' :
        msg.includes('subject_required') ? 'Subject is required.' :
        'Could not post. Please try again.'
      );
      return;
    }

    // Same ordering rule as the video suggestion below: the post is already
    // up, so a failed photo attach costs the photos, not the post.
    let imageError = '';
    if (newPostId && images.length) {
      const { error: imgErr } = await syncContentImages({ postId: newPostId, images });
      if (imgErr) imageError = imageErrorMessage(imgErr.message);
    }

    // After the post exists, never before: a failed suggestion must not cost
    // the member their post. The outcome rides along to the post page.
    const suggestionResult = newPostId && videoSuggestion
      ? await suggestVideoFromPost({ ...videoSuggestion, postId: newPostId })
      : null;
    setSubmitting(false);

    clearDraft(DRAFT_KEY);
    onClose();
    if (newPostId) {
      navigate(`/community/${selectedTable.slug}/${newPostId}`, {
        state: (suggestionResult || imageError)
          ? { videoSuggestion: suggestionResult ?? undefined, imageError: imageError || undefined }
          : undefined,
      });
    }
  };

  const SkeletonRows = () => (
    <>
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 rounded w-2/5" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
            <div className="h-3 rounded w-1/4" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
          </div>
        </div>
      ))}
    </>
  );

  // ── Step: Type picker ──────────────────────────────────────
  const typePicker = (
    <div className="flex flex-col">
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--card-border)' }}
      >
        <h2 className="text-base font-bold text-primary">Create a post</h2>
        <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
          <X size={20} style={{ color: 'var(--text-tertiary)' }} />
        </button>
      </div>

      <div className="px-4 py-5 space-y-3">
        {/* Table Post */}
        <button
          onClick={() => setStep('table')}
          className="w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <MessageSquare size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-primary">Table Post</p>
            <p className="text-xs mt-0.5 leading-snug" style={{ color: 'var(--text-tertiary)' }}>
              Start or join a conversation in a community table
            </p>
          </div>
        </button>

        {/* Video Post */}
        <button
          onClick={() => { onClose(); navigate('/submit-video'); }}
          className="w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--brand-accent)' }}
          >
            <Video size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-primary">Video Post</p>
            <p className="text-xs mt-0.5 leading-snug" style={{ color: 'var(--text-tertiary)' }}>
              Share a YouTube video with the community
            </p>
          </div>
        </button>
      </div>
    </div>
  );

  // ── Step: Table picker ─────────────────────────────────────
  const tablePicker = (
    <div className="flex flex-col" style={{ maxHeight: isDesktop ? '75vh' : '88vh' }}>
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--card-border)' }}
      >
        {/* When the chooser was skipped there's nothing to go back to — close instead */}
        <button
          onClick={() => (startStep === 'table' ? onClose() : setStep('type'))}
          className="p-1 rounded-lg hover-surface"
        >
          <ChevronLeft size={20} style={{ color: 'var(--text-tertiary)' }} />
        </button>
        <h2 className="text-base font-bold text-primary">Post to</h2>
        <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
          <X size={20} style={{ color: 'var(--text-tertiary)' }} />
        </button>
      </div>

      <div className="px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={inputStyle}>
          <Search size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search tables..."
            value={tableSearch}
            onChange={e => setTableSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-primary placeholder:text-tertiary focus:outline-none"
            autoFocus
          />
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {tablesLoading ? (
          <SkeletonRows />
        ) : filteredTables.length === 0 ? (
          <p className="text-sm text-tertiary text-center py-8">No tables found</p>
        ) : (
          filteredTables.map(table => (
            <button
              key={table.id}
              onClick={() => { setSelectedTable(table); setStep('compose'); }}
              className="w-full flex items-center gap-3 px-4 py-3 transition-all duration-200 text-left"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--surface-hover)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-base"
                style={{ backgroundColor: 'var(--pill-bg)' }}
              >
                {table.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-primary leading-tight">{table.name}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  {table.member_count ?? 0} members{table.is_member ? ' · Joined' : ''}
                </p>
              </div>
            </button>
          ))
        )}
        <div className="h-4" />
      </div>
    </div>
  );

  // ── Step: Compose ──────────────────────────────────────────
  const compose = selectedTable && (
    <div className="flex flex-col" style={{ maxHeight: isDesktop ? '75vh' : '88vh' }}>
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--card-border)' }}
      >
        <button onClick={() => setStep('table')} className="p-1 rounded-lg hover-surface">
          <ChevronLeft size={20} style={{ color: 'var(--text-tertiary)' }} />
        </button>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-base flex-shrink-0">{selectedTable.emoji}</span>
          <span className="font-semibold text-sm text-primary truncate max-w-[160px]">
            {selectedTable.name}
          </span>
        </div>
        <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
          <X size={20} style={{ color: 'var(--text-tertiary)' }} />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 px-4 py-4 space-y-3">
        {restored && (
          <div
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-xs"
            style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--text-tertiary)' }}
          >
            <span>Draft restored.</span>
            <button
              onClick={discardDraft}
              className="font-semibold flex-shrink-0 transition-opacity hover:opacity-70"
              style={{ color: 'var(--brand-accent)' }}
            >
              Start over
            </button>
          </div>
        )}
        <div>
          <input
            type="text"
            placeholder="Subject *"
            value={subject}
            onChange={e => setSubject(e.target.value.slice(0, 200))}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-primary focus:outline-none focus:ring-2"
            style={inputStyle}
            autoFocus
          />
          <p className="text-xs mt-1 text-right" style={{ color: 'var(--text-tertiary)' }}>
            {subject.length}/200
          </p>
        </div>
        <PostEditor
          placeholder="What's on your mind? *"
          ariaLabel="Message"
          value={content}
          onChange={setContent}
          maxLength={5000}
          onSubmit={handleSubmit}
          images={images}
          onImagesChange={setImages}
          userId={userId}
        />
        <TagInput value={tags} onChange={setTags} />
        <SuggestVideoToLibrary
          subject={subject}
          content={content}
          value={videoChoice}
          onChange={setVideoChoice}
          onResolve={handleVideoResolve}
        />
        {error && (
          <p className="text-xs text-center" style={{ color: 'var(--color-error)' }}>{error}</p>
        )}
        <button
          onClick={handleSubmit}
          disabled={submitting || !subject.trim() || !content.trim()}
          className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Posting...
            </>
          ) : 'Post'}
        </button>
      </div>
    </div>
  );

  const modalContent =
    step === 'type' ? typePicker :
    step === 'table' ? tablePicker :
    compose;

  // ── Desktop: centered modal ────────────────────────────────
  if (isDesktop) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden animate-fadeIn"
          style={cardStyle}
          onClick={e => e.stopPropagation()}
        >
          {modalContent}
        </div>
      </div>
    );
  }

  // ── Mobile: bottom sheet ───────────────────────────────────
  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="absolute inset-x-0 bottom-0 rounded-t-2xl overflow-hidden animate-fadeIn"
        style={{ ...cardStyle, maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: 'var(--card-border)' }} />
        </div>
        {modalContent}
      </div>
    </div>
  );
};

export default CreatePostModal;
